import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { dogInfo, pricing, testimonials, images, contactInfo } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="TARZAN - Labrador Chocolat LOF"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
            <span className="text-[#cbb26a]">TARZAN</span>
            <br />
            <span className="text-3xl md:text-5xl">Labrador Chocolat LOF</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
            Mâle reproducteur d'exception à Sainte-Olive 01330 • 3 ans • Tests de santé OK
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              asChild
              size="lg"
              className="bg-[#cbb26a] text-black hover:bg-[#d4c485] transition-all duration-300 text-lg px-8 py-6"
            >
              <a href={`https://wa.me/33${contactInfo.whatsapp.substring(1)}`} className="flex items-center space-x-2">
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-[#cbb26a] text-white hover:bg-[#cbb26a]/10 transition-all duration-300 text-lg px-8 py-6"
            >
              <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-2">
                <Phone size={20} />
                <span>{contactInfo.phone}</span>
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-20 bg-gradient-to-b from-black to-black/95">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#cbb26a] mb-2">{dogInfo.age} ans</div>
                <div className="text-white/70">Âge</div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#cbb26a] mb-2">{dogInfo.weight} kg</div>
                <div className="text-white/70">Poids</div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#cbb26a] mb-2">{dogInfo.heightBack} cm</div>
                <div className="text-white/70">Hauteur au dos</div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-[#cbb26a] mb-2">{pricing.studFee}€</div>
                <div className="text-white/70">Tarif saillie</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Un <span className="text-[#cbb26a]">reproducteur</span> d'exception
              </h2>
              <p className="text-white/80 text-lg mb-6 leading-relaxed">
                Tarzan est un magnifique Labrador chocolat LOF de {dogInfo.age} ans, {dogInfo.description.toLowerCase()}. 
                Avec ses {dogInfo.heightBack} cm au dos et {dogInfo.length} cm de longueur, il présente une morphologie 
                parfaite conforme au standard de la race.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#cbb26a]" size={24} />
                  <span className="text-white/90">Tests de dysplasie : {dogInfo.health.dysplasia}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#cbb26a]" size={24} />
                  <span className="text-white/90">Tests ADN : {dogInfo.health.dna}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#cbb26a]" size={24} />
                  <span className="text-white/90">Inscrit au LOF</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#cbb26a]" size={24} />
                  <span className="text-white/90">Tempérament équilibré</span>
                </div>
              </div>
              <Button
                asChild
                size="lg"
                className="bg-[#cbb26a] text-black hover:bg-[#d4c485] transition-all duration-300"
              >
                <Link to="/reproducteur" className="flex items-center space-x-2">
                  <span>En savoir plus</span>
                  <ArrowRight size={20} />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={images.gallery[0]}
                alt="Tarzan"
                className="w-full h-64 object-cover rounded-lg"
              />
              <img
                src={images.gallery[1]}
                alt="Tarzan"
                className="w-full h-64 object-cover rounded-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Section */}
      <section className="py-20 bg-black/95">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Conditions de <span className="text-[#cbb26a]">saillie</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Un service professionnel avec toutes les garanties nécessaires
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#cbb26a] mb-4">Tarif</h3>
                <p className="text-3xl font-bold text-white mb-2">{pricing.studFee}€</p>
                <p className="text-white/70 text-sm">2 saillies incluses</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#cbb26a] mb-4">Garantie</h3>
                <p className="text-white/90 mb-2">Re-saillie gratuite</p>
                <p className="text-white/70 text-sm">En cas d'échec</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#cbb26a] mb-4">Service</h3>
                <p className="text-white/90 mb-2">Accueil professionnel</p>
                <p className="text-white/70 text-sm">Suivi personnalisé</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-10">
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-[#cbb26a] text-white hover:bg-[#cbb26a]/10 transition-all duration-300"
            >
              <Link to="/conditions">Voir toutes les conditions</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <span className="text-[#cbb26a]">Témoignages</span> clients
            </h2>
            <p className="text-white/70 text-lg">Ce que disent nos clients</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/5 border-[#cbb26a]/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="fill-[#cbb26a] text-[#cbb26a]" />
                    ))}
                  </div>
                  <p className="text-white/80 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t border-[#cbb26a]/20 pt-4">
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-white/60 text-sm">{testimonial.location} • {testimonial.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-[#cbb26a] text-white hover:bg-[#cbb26a]/10 transition-all duration-300"
            >
              <Link to="/temoignages">Voir tous les témoignages</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-black/95">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à réserver une <span className="text-[#cbb26a]">saillie</span> ?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Contactez-nous dès maintenant pour réserver votre date ou obtenir plus d'informations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              asChild
              size="lg"
              className="bg-[#cbb26a] text-black hover:bg-[#d4c485] transition-all duration-300 text-lg px-8 py-6"
            >
              <Link to="/contact" className="flex items-center space-x-2">
                <span>Formulaire de contact</span>
                <ArrowRight size={20} />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-[#cbb26a] text-white hover:bg-[#cbb26a]/10 transition-all duration-300 text-lg px-8 py-6"
            >
              <a href={`https://wa.me/33${contactInfo.whatsapp.substring(1)}`} className="flex items-center space-x-2">
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;